// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  makeStyles,
  tokens,
  Divider,
  Button,
  Title1,
  Body1,
  Caption1,
} from "@fluentui/react-components";
import {
  ArrowRight24Regular,
  ShieldLock24Regular,
  Sparkle24Regular,
} from "@fluentui/react-icons";
import { useLogin } from "../hooks/useLogin";
import { navigateTo } from "../../../router/navigation";
import { isDevPreviewMode } from "../../../utils/devEnv";

const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXL}`,
    boxSizing: "border-box",
    background:
      "linear-gradient(145deg, var(--riddle-bg-top), var(--riddle-bg-bottom))",
    overflow: "hidden",
    "@media (max-width: 480px)": {
      padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalM}`,
    },
  },
  glow: {
    position: "absolute",
    width: "480px",
    height: "480px",
    borderRadius: "50%",
    background: "var(--riddle-radial-1)",
    filter: "blur(90px)",
    pointerEvents: "none",
  },
  card: {
    position: "relative",
    width: "100%",
    maxWidth: "440px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: tokens.spacingVerticalL,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL}`,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow16,
    minWidth: 0,
    "@media (max-width: 480px)": {
      padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalL}`,
    },
  },
  brand: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingHorizontalM,
  },
  brandMark: {
    width: "56px",
    height: "56px",
    borderRadius: tokens.borderRadiusLarge,
    display: "block",
    flexShrink: 0,
  },
  brandWordmark: {
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    letterSpacing: "-0.02em",
  },
  heading: {
    color: tokens.colorNeutralForeground1,
    lineHeight: "1.2",
    textAlign: "center",
    width: "100%",
  },
  description: {
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
  },
  copyBlock: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    width: "100%",
  },
  divider: {
    width: "100%",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    width: "100%",
  },
  button: {
    width: "100%",
    justifyContent: "center",
    fontWeight: tokens.fontWeightSemibold,
    minHeight: "40px",
    whiteSpace: "normal",
    height: "auto",
  },
  status: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    minHeight: "18px",
    overflowWrap: "anywhere",
  },
  footer: {
    color: tokens.colorNeutralForeground4,
    textAlign: "center",
  },
});

function BrandHeader({ styles }: { styles: ReturnType<typeof useStyles> }) {
  return (
    <div className={styles.brand}>
      <img
        src="/riddle-brand-icon.png"
        alt=""
        aria-hidden="true"
        className={styles.brandMark}
      />
      <span className={styles.brandWordmark}>Riddle</span>
    </div>
  );
}

function DevPreviewWelcome({ styles }: { styles: ReturnType<typeof useStyles> }) {
  return (
    <div className={styles.card}>
      <BrandHeader styles={styles} />

      <div className={styles.copyBlock}>
        <Title1 className={styles.heading} block>
          Welcome to Riddle
        </Title1>
        <Body1 className={styles.description} block>
          Your real estate workspace — contacts, journeys, communications, and
          property operations in one place.
        </Body1>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.actions}>
        <Button
          className={styles.button}
          appearance="primary"
          size="large"
          icon={<ArrowRight24Regular />}
          iconPosition="after"
          onClick={() => navigateTo("/insights")}
        >
          Enter Workspace
        </Button>
        <Button
          className={styles.button}
          appearance="secondary"
          size="large"
          icon={<Sparkle24Regular />}
          onClick={() => navigateTo("/onboarding")}
        >
          Preview Onboarding
        </Button>
      </div>

      <Divider className={styles.divider} />

      <Caption1 className={styles.footer}>
        Riddle platform — unified real estate workspace
      </Caption1>
    </div>
  );
}

export function LoginPage() {
  const styles = useStyles();
  const { statusMessage, isBusy, isConfigured, handleLogin } = useLogin();

  if (isDevPreviewMode) {
    return (
      <div className={styles.root}>
        <span className={styles.glow} aria-hidden="true" />
        <DevPreviewWelcome styles={styles} />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <span className={styles.glow} aria-hidden="true" />
      <div className={styles.card}>
        <BrandHeader styles={styles} />

        <div className={styles.copyBlock}>
          <Title1 className={styles.heading} block>
            Welcome Back
          </Title1>
          <Body1 className={styles.description} block>
            Sign in to your Riddle workspace with your organizational account.
          </Body1>
        </div>

        <Divider className={styles.divider} />

        {isConfigured ? (
          <div className={styles.actions}>
            <Button
              className={styles.button}
              appearance="primary"
              size="large"
              icon={<ShieldLock24Regular />}
              disabled={isBusy}
              onClick={() => void handleLogin()}
            >
              Sign In
            </Button>
          </div>
        ) : null}

        <Caption1 className={styles.status} role="status" aria-live="polite">
          {statusMessage}
        </Caption1>

        <Divider className={styles.divider} />

        <Caption1 className={styles.footer}>
          Riddle platform — unified real estate workspace
        </Caption1>
      </div>
    </div>
  );
}
